/**
 * @module TitleBar
 */

import {
  clickButton,
  getSafeButtonProps,
  isValidButtonProps,
  validationError as buttonValidationError,
  ActionType as ButtonActionType,
  Button,
  Payload as ButtonPayload,
} from '../Button';
import {
  getSafeButtonGroupProps,
  isGroupedButtonPayload,
  isValidButtonGroupProps,
  validationError as buttonGroupValidationError,
  ActionType as ButtonGroupActionType,
  ButtonGroup,
  Payload as ButtonGroupPayload,
} from '../ButtonGroup';

import {ClientApplication} from '../../client';
import {getGroupedButton} from '../buttonGroupHelper';
import {getSingleButton} from '../buttonHelper';
import {
  actionWrapper,
  getMergedProps,
  updateActionFromPayload,
  ActionSetWithChildren,
} from '../helper';
import {ActionSetProps, ClickAction, ComponentType, Group, MetaAction} from '../types';
import {
  invalidActionType,
  invalidPayload,
  throwError,
  ActionType as ErrorActionType,
  ErrorAction,
  Message as ErrorMessage,
} from '../Error';
import {
  Action,
  ActionType,
  ButtonsActionType as TitleBarButtonActionType,
  ButtonsOptions as TitleBarButtonsOptions,
  ButtonsPayload as TitleBarButtonsPayload,
  Options,
  Payload,
} from './types';
export const GROUP = Group.TitleBar;

const TITLEBAR_BUTTON_PROPS = {
  group: GROUP,
  subgroups: ['Buttons'],
};

const BREADCRUMB_BUTTON_PROPS = {
  group: GROUP,
  subgroups: ['Breadcrumbs'],
  type: ComponentType.Button,
};

export interface UpdateAction extends MetaAction {
  readonly group: typeof GROUP;
  payload: Payload;
}

export type TitleBarAction = UpdateAction | ClickAction | MetaAction;

export function clickActionButton(id: string, payload?: any): ClickAction {
  const type = ComponentType.Button;
  const component = {id, type, ...TITLEBAR_BUTTON_PROPS};

  return clickButton(GROUP, component, payload);
}

export function clickBreadcrumb(id: string, payload?: any): ClickAction {
  const component = {id, ...BREADCRUMB_BUTTON_PROPS};

  return clickButton(GROUP, component, payload);
}

export function update(payload: Payload): UpdateAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.UPDATE,
  });
}

export function isValidTitleBarPayload(payload: Payload) {
  if (!payload) {
    return false;
  }

  return (
    (payload.id === undefined || typeof payload.id === 'string') && isValidTitleBarProps(payload)
  );
}

export function isValidTitleBarProps(props: Payload | Options) {
  return (
    (typeof props.title === 'string' || props.title === undefined) &&
    isValidTitleBarButtons(props.buttons) &&
    isValidTitleBarBreadcrumbs(props.breadcrumbs)
  );
}

export function isValidTitleBarButtons(buttons?: TitleBarButtonsPayload) {
  if (buttons === undefined) {
    return true;
  }

  if (typeof buttons !== 'object' || Array.isArray(buttons)) {
    return false;
  }

  const primary = buttons.primary;
  const secondary = buttons.secondary;

  if (primary && !isValidButtonProps(primary)) {
    return false;
  }

  if (secondary !== undefined && !Array.isArray(secondary)) {
    return false;
  }

  if (Array.isArray(secondary)) {
    for (const button of secondary) {
      if (isGroupedButtonPayload(button) && isValidButtonGroupProps(button)) {
        continue;
      } else if (!isGroupedButtonPayload(button) && isValidButtonProps(button)) {
        continue;
      }

      return false;
    }
  }

  return true;
}

export function isValidTitleBarBreadcrumbs(button?: ButtonPayload) {
  if (button === undefined || isValidButtonProps(button)) {
    return true;
  }

  return false;
}

export function validationError(action: TitleBarAction): undefined | ErrorAction {
  let payload;
  switch (action.type) {
    case ActionType.UPDATE:
      payload = (action as UpdateAction).payload;
      if (payload === undefined) {
        return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
      }

      if (typeof payload.id !== 'string') {
        return invalidPayload(action, ErrorMessage.INVALID_PAYLOAD_ID);
      }

      return isValidTitleBarPayload(payload) ? undefined : invalidPayload(action);

    case TitleBarButtonActionType.BUTTON_CLICK:
    case TitleBarButtonActionType.BREADCRUMBS_CLICK:
      return buttonValidationError({...action, type: ButtonActionType.CLICK});

    case TitleBarButtonActionType.BUTTON_UPDATE:
    case TitleBarButtonActionType.BREADCRUMBS_UPDATE:
      return buttonValidationError({...action, type: ButtonActionType.UPDATE});

    case TitleBarButtonActionType.BUTTON_GROUP_UPDATE:
      return buttonGroupValidationError({...action, type: ButtonGroupActionType.UPDATE});

    default:
      return invalidActionType(action);
  }
}

export class TitleBar extends ActionSetWithChildren implements ActionSetProps<Options, Payload> {
  title?: string;
  primary?: ButtonPayload;
  secondary?: (ButtonPayload | ButtonGroupPayload)[];
  primaryOptions?: Button;
  secondaryOptions?: (ButtonGroup | Button)[];
  breadcrumb?: ButtonPayload;
  breadcrumbsOption?: Button;

  constructor(app: ClientApplication<any>, options: Options) {
    super(app, GROUP, GROUP);
    // Trigger 'update' on creation
    this.set(options);
  }

  get buttons(): TitleBarButtonsPayload | undefined {
    if (!this.primary && !this.secondary) {
      return undefined;
    }

    return {
      primary: this.primary,
      secondary: this.secondary,
    };
  }

  get buttonsOptions(): TitleBarButtonsOptions | undefined {
    if (!this.primaryOptions && !this.secondaryOptions) {
      return undefined;
    }

    return {
      primary: this.primaryOptions,
      secondary: this.secondaryOptions,
    };
  }

  get options(): Options {
    return {
      breadcrumbs: this.breadcrumbsOption,
      buttons: this.buttonsOptions,
      title: this.title,
    };
  }

  get payload(): Payload {
    return {
      ...this.options,
      breadcrumbs: this.breadcrumb,
      buttons: this.buttons,
      id: this.id,
    };
  }

  set(options: Partial<Options>, shouldUpdate = true) {
    const mergedOptions: Options = getMergedProps(this.options, options);
    const {title, buttons, breadcrumbs} = mergedOptions;
    if (!this.validateOptions(mergedOptions)) {
      return this;
    }

    this.title = title;
    this.setBreadcrumbs(breadcrumbs);
    this.setPrimaryButton(buttons ? buttons.primary : undefined);
    this.setSecondaryButton(buttons ? buttons.secondary : undefined);

    if (shouldUpdate) {
      this.dispatch(Action.UPDATE);
    }

    return this;
  }

  dispatch(action: Action) {
    switch (action) {
      case Action.UPDATE:
        this.app.dispatch(update(this.payload));
        break;
      default:
        throwError(
          ErrorActionType.INVALID_ACTION_TYPE,
          actionWrapper({
            group: this.group,
            type: action,
          }),
        );
    }

    return this;
  }

  protected getButton(
    button: Button | ButtonGroup,
    subgroups: string[],
    updateCb: (newPayload: ButtonPayload | ButtonGroupPayload) => void,
  ): ButtonPayload | ButtonGroupPayload {
    if (button instanceof ButtonGroup) {
      return getGroupedButton(this, button, subgroups, updateCb);
    }

    return getSingleButton(this, button, subgroups, updateCb);
  }

  protected updatePrimaryButton(newPayload: ButtonPayload) {
    if (!this.primary) {
      return;
    }
    if (updateActionFromPayload(this.primary, getSafeButtonProps(newPayload))) {
      this.dispatch(Action.UPDATE);
    }
  }

  protected updateSecondaryButtons(newPayload: ButtonPayload | ButtonGroupPayload) {
    if (!this.secondary) {
      return;
    }
    const buttonToUpdate = this.secondary.find(action => action.id === newPayload.id);
    if (!buttonToUpdate) {
      return;
    }

    let updated = false;
    if (isGroupedButtonPayload(newPayload)) {
      updated = updateActionFromPayload(buttonToUpdate, getSafeButtonGroupProps(newPayload));
    } else {
      updated = updateActionFromPayload(buttonToUpdate, getSafeButtonProps(newPayload));
    }
    if (updated) {
      this.dispatch(Action.UPDATE);
    }
  }

  protected updateBreadcrumbButton(newPayload: ButtonPayload) {
    if (!this.breadcrumb) {
      return;
    }
    if (updateActionFromPayload(this.breadcrumb, getSafeButtonProps(newPayload))) {
      this.dispatch(Action.UPDATE);
    }
  }

  protected setPrimaryButton(newOptions?: Button) {
    this.primaryOptions = this.getChildButton(newOptions, this.primaryOptions);
    this.primary = this.primaryOptions
      ? this.getButton(
          this.primaryOptions,
          TITLEBAR_BUTTON_PROPS.subgroups,
          this.updatePrimaryButton,
        )
      : undefined;
  }

  protected setSecondaryButton(newOptions?: (ButtonGroup | Button)[]) {
    const newButtons = newOptions || [];
    const currentButtons = this.secondaryOptions || [];
    this.secondaryOptions = this.getUpdatedChildActions(newButtons, currentButtons);

    this.secondary = this.secondaryOptions
      ? this.secondaryOptions.map(action =>
          this.getButton(action, TITLEBAR_BUTTON_PROPS.subgroups, this.updateSecondaryButtons),
        )
      : undefined;
  }

  protected setBreadcrumbs(breadcrumb?: Button) {
    this.breadcrumbsOption = this.getChildButton(breadcrumb, this.breadcrumbsOption);
    this.breadcrumb = this.breadcrumbsOption
      ? this.getButton(
          this.breadcrumbsOption,
          BREADCRUMB_BUTTON_PROPS.subgroups,
          this.updateBreadcrumbButton,
        )
      : undefined;
  }

  protected getChildButton(newAction: undefined | Button, currentAction: undefined | Button) {
    const newButtons = newAction ? [newAction] : [];
    const currentButtons = currentAction ? [currentAction] : [];
    const updatedButton = this.getUpdatedChildActions(newButtons, currentButtons);

    return updatedButton ? updatedButton[0] : undefined;
  }

  private validateOptions(options: Options) {
    const props = {id: this.id, ...options};
    if (!isValidTitleBarProps(options)) {
      const errorAction = update(props);
      throwError(ErrorActionType.INVALID_OPTIONS, errorAction);

      return false;
    }

    return true;
  }
}

export function create(app: ClientApplication<any>, options: Options) {
  return new TitleBar(app, options);
}



// WEBPACK FOOTER //
// ./src/actions/TitleBar/actions.ts